// frontend/src/store/vtuStore.js

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import vtuAPI from '../services/vtu.api.js';
import { v4 as uuidv4 } from 'uuid';

const useVTUStore = create(
  persist(
    (set, get) => ({
      products: [],
      dataPlans: {},
      transactions: [],
      transactionSummary: null,
      recentRecipients: [],
      isLoading: false,
      error: null,

      // Action: Get products
      getProducts: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await vtuAPI.getProducts(params);
          set({ products: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Detect network
      detectNetwork: async (phone) => {
        set({ isLoading: true, error: null });
        try {
          const response = await vtuAPI.detectNetwork(phone);
          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Purchase airtime
      purchaseAirtime: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const reference = `AIR_${Date.now()}_${uuidv4().slice(0, 8)}`;
          const purchaseData = { ...data, reference };

          const response = await vtuAPI.purchaseAirtime(purchaseData);

          if (response.success) {
            get().addRecentRecipientRealTime(data.phone);
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get data plans
      getDataPlans: async (network) => {
        set({ isLoading: true, error: null });
        try {
          const response = await vtuAPI.getDataPlans(network, { _t: Date.now() });

          let plans = [];

          if (response.data && response.data.plans) {
            plans = response.data.plans;
          } else if (response.data && Array.isArray(response.data)) {
            plans = response.data;
          } else if (response.plans) {
            plans = response.plans;
          }

          set((state) => ({
            dataPlans: {
              ...state.dataPlans,
              [network]: plans,
            },
          }));

          return plans;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Purchase data
      purchaseData: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const reference = `DATA_${Date.now()}_${uuidv4().slice(0, 8)}`;
          const purchaseData = { ...data, reference };

          const response = await vtuAPI.purchaseData(purchaseData);

          if (response.success) {
            get().addRecentRecipientRealTime(data.phone);
          }

          return response;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get transactions
      getTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await vtuAPI.getTransactions(params);
          set({ transactions: response.data.transactions });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Action: Get transaction summary
      getTransactionSummary: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await vtuAPI.getTransactionSummary();
          set({ transactionSummary: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // Real-time update methods (no toasts — socket handlers or pages decide)
      addTransactionRealTime: (transaction) => {
        set((state) => ({
          transactions: [transaction, ...state.transactions.slice(0, 49)],
          transactionSummary: state.transactionSummary
            ? {
                ...state.transactionSummary,
                airtime:
                  transaction.serviceType === 'AIRTIME'
                    ? {
                        totalSpent:
                          (state.transactionSummary.airtime?.totalSpent || 0) +
                          transaction.amount,
                        transactionCount:
                          (state.transactionSummary.airtime?.transactionCount || 0) +
                          1,
                      }
                    : state.transactionSummary.airtime,
                data:
                  transaction.serviceType === 'DATA'
                    ? {
                        totalSpent:
                          (state.transactionSummary.data?.totalSpent || 0) +
                          transaction.amount,
                        transactionCount:
                          (state.transactionSummary.data?.transactionCount || 0) +
                          1,
                      }
                    : state.transactionSummary.data,
                recentTransactions: [
                  transaction,
                  ...(state.transactionSummary.recentTransactions || []).slice(0, 9),
                ],
              }
            : state.transactionSummary,
        }));
      },

      updateTransactionStatus: (transactionId, status, data = {}) => {
        set((state) => ({
          transactions: state.transactions.map((tx) =>
            tx.id === transactionId ? { ...tx, status, ...data } : tx
          ),
        }));
      },

      handleTransactionPending: (data) => {
        set((state) => {
          const exists = state.transactions.some((tx) => tx.id === data.transactionId);
          if (!exists) {
            return { transactions: [data, ...state.transactions] };
          }
          return state;
        });
      },

      handleTransactionSuccess: (data) => {
        set((state) => ({
          transactions: state.transactions.map((tx) =>
            tx.id === data.transactionId ? { ...tx, status: 'SUCCESS', ...data } : tx
          ),
          transactionSummary: state.transactionSummary
            ? {
                ...state.transactionSummary,
                airtime:
                  data.serviceType === 'AIRTIME'
                    ? {
                        totalSpent:
                          (state.transactionSummary.airtime?.totalSpent || 0) +
                          data.amount,
                        transactionCount:
                          (state.transactionSummary.airtime?.transactionCount || 0) +
                          1,
                      }
                    : state.transactionSummary.airtime,
                data:
                  data.serviceType === 'DATA'
                    ? {
                        totalSpent:
                          (state.transactionSummary.data?.totalSpent || 0) +
                          data.amount,
                        transactionCount:
                          (state.transactionSummary.data?.transactionCount || 0) +
                          1,
                      }
                    : state.transactionSummary.data,
              }
            : state.transactionSummary,
        }));
      },

      handleTransactionFailed: (data) => {
        set((state) => ({
          transactions: state.transactions.map((tx) =>
            tx.id === data.transactionId
              ? { ...tx, status: 'FAILED', failureReason: data.reason }
              : tx
          ),
        }));
      },

      handleRetrySuccess: (data) => {
        get().handleTransactionSuccess(data);
      },

      handleRetryFailed: (data) => {
        get().handleTransactionFailed(data);
      },

      // Add recipient to recent list
      addRecentRecipientRealTime: (phone) => {
        set((state) => ({
          recentRecipients: [
            phone,
            ...state.recentRecipients.filter((r) => r !== phone),
          ].slice(0, 5),
        }));
      },

      // Clear error
      clearError: () => set({ error: null }),

      // Add recent recipient (legacy method)
      addRecentRecipient: (phone) => {
        get().addRecentRecipientRealTime(phone);
      },
    }),
    {
      name: 'vtu-storage',
      partialize: (state) => ({
        recentRecipients: state.recentRecipients,
        dataPlans: state.dataPlans,
      }),
    }
  )
);

export default useVTUStore;